import type { Cents } from "../domain/money";

/**
 * WHERE A PROJECT STANDS, AND WHAT TO DO NEXT (spec §86).
 *
 * Every answer here is DERIVED from what the project actually holds — an
 * address, rooms, walls, scope, priced lines, a bid, a receipt. Nothing is a
 * checkbox somebody ticked, so a project imported years ago, a project built
 * entirely by drawing, and a project somebody typed in by hand all report
 * truthfully without a migration, and a returning user resumes exactly where
 * the data says they stopped.
 *
 * Pure and deterministic: no I/O, no clock. The repository gathers a
 * snapshot; this decides what it means. The same function feeds the
 * in-app checklist and the server's activation milestones, so the admin
 * funnel and the person using the app can never disagree.
 */

export interface ActivationSnapshot {
  projectId: string;
  projectName: string;
  projectCreatedAt: string;
  updatedAt: string;
  hasAddress: boolean;
  hasPurchasePrice: boolean;
  hasLivingArea: boolean;
  /** Live rooms on live floors. */
  roomCount: number;
  firstRoomAt: string | null;
  /** Live rooms carrying a floor area — typed, or measured from a drawing. */
  measuredRoomCount: number;
  /** Drawn walls (never logical boundaries) in the project's live designs. */
  wallCount: number;
  firstWallAt: string | null;
  /** Live work items in the plan the estimate follows (common + Active). */
  scopeItemCount: number;
  firstScopeAt: string | null;
  /** Of those, the items whose own lines add up to more than nothing. */
  pricedScopeItemCount: number;
  pricedScopeCents: Cents;
  firstPricedAt: string | null;
  /** Everything the working estimate holds, manual lines included. */
  estimateCents: Cents;
  bidPackageCount: number;
  quoteCount: number;
  firstBidAt: string | null;
  /** Receipts, expenses and mileage recorded against the project. */
  actualEventCount: number;
  firstActualAt: string | null;
}

export type ActivationStepId = "property" | "rooms" | "scope" | "estimate" | "bids" | "actuals";

/** Where a step leads: the tab that does the work, and the button's words. */
export interface ActivationAction {
  tab: "overview" | "design" | "scope" | "bids" | "accounting";
  label: string;
}

export interface ActivationStep {
  id: ActivationStepId;
  title: string;
  /** done · next (the one thing to do now) · todo (later) · optional (not needed to reach an estimate) */
  state: "done" | "next" | "todo" | "optional";
  /** One plain sentence about THIS project — never a generic instruction. */
  detail: string;
  action: ActivationAction;
}

export interface Activation {
  steps: ActivationStep[];
  next: ActivationStep | null;
  /** The activation event itself: see {@link firstEstimateReached}. */
  firstEstimateReached: boolean;
  /** Done steps among the four that lead to an estimate. */
  setupDone: number;
  setupTotal: number;
}

/**
 * THE ACTIVATION EVENT, DEFINED ONCE (spec §86-9).
 *
 * A first estimate is reached when the working estimate contains at least
 * one piece of RENOVATION SCOPE whose own cost lines add up to more than $0.
 *
 * Deliberately not "the total is above zero": a single hand-typed line with
 * no work behind it is a number, not an estimate, and an empty project is
 * $0.00. Requiring priced SCOPE means the person has said what work is
 * happening and Berrier Reno (or they) priced it — which is the moment the
 * app has shown its value.
 */
export function firstEstimateReached(snapshot: ActivationSnapshot): boolean {
  return snapshot.pricedScopeItemCount > 0 && snapshot.pricedScopeCents > 0;
}

/** The milestones the funnel measures, in the order a project reaches them. */
export const ACTIVATION_MILESTONES = [
  "project_created",
  "project_property_configured",
  "room_created",
  "design_started",
  "scope_created",
  "first_estimate_reached",
  "contractor_bid_package_created",
  "contractor_quote_recorded",
  "actual_cost_recorded",
] as const;

export type ActivationMilestone = (typeof ACTIVATION_MILESTONES)[number];

/**
 * Which milestones this project has reached, each with the EARLIEST moment
 * the data itself can evidence (a room's own creation time, a line's) —
 * never the moment somebody happened to look. A milestone with no evidencing
 * timestamp falls back to the project's own update time.
 */
export function reachedMilestones(snapshot: ActivationSnapshot): Array<{ milestone: ActivationMilestone; at: string }> {
  const out: Array<{ milestone: ActivationMilestone; at: string }> = [];
  const push = (milestone: ActivationMilestone, at: string | null) =>
    out.push({ milestone, at: at ?? snapshot.updatedAt });
  push("project_created", snapshot.projectCreatedAt);
  if (propertyConfigured(snapshot)) push("project_property_configured", snapshot.updatedAt);
  if (snapshot.roomCount > 0) push("room_created", snapshot.firstRoomAt);
  if (snapshot.wallCount > 0) push("design_started", snapshot.firstWallAt);
  if (snapshot.scopeItemCount > 0) push("scope_created", snapshot.firstScopeAt);
  if (firstEstimateReached(snapshot)) push("first_estimate_reached", snapshot.firstPricedAt);
  if (snapshot.bidPackageCount > 0) push("contractor_bid_package_created", snapshot.firstBidAt);
  if (snapshot.quoteCount > 0) push("contractor_quote_recorded", snapshot.firstBidAt);
  if (snapshot.actualEventCount > 0) push("actual_cost_recorded", snapshot.firstActualAt);
  return out;
}

/** An address plus something that says what the property is worth or how big it is. */
export function propertyConfigured(snapshot: ActivationSnapshot): boolean {
  return snapshot.hasAddress && (snapshot.hasPurchasePrice || snapshot.hasLivingArea);
}

const money = (cents: Cents): string =>
  `$${Math.round(cents / 100).toLocaleString("en-US")}`;

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function deriveActivation(snapshot: ActivationSnapshot): Activation {
  const estimated = firstEstimateReached(snapshot);
  const hasRooms = snapshot.roomCount > 0 || snapshot.wallCount > 0;
  const hasScope = snapshot.scopeItemCount > 0;

  const property: ActivationStep = {
    id: "property",
    title: "The property",
    state: snapshot.hasAddress ? "done" : "todo",
    detail: snapshot.hasAddress
      ? propertyConfigured(snapshot)
        ? "Address and property details are in."
        : "Address is in. Adding the purchase price or living area makes the numbers more useful."
      : "Add the property's address.",
    action: { tab: "overview", label: snapshot.hasAddress ? "Edit property" : "Add the address" },
  };

  const rooms: ActivationStep = {
    id: "rooms",
    title: "The rooms",
    state: hasRooms ? "done" : "todo",
    detail: hasRooms
      ? snapshot.wallCount > 0
        ? `The house is drawn${snapshot.roomCount > 0 ? ` with ${plural(snapshot.roomCount, "room")}` : ""}.`
        : `${plural(snapshot.roomCount, "room")} added${snapshot.measuredRoomCount < snapshot.roomCount ? ` — ${plural(snapshot.roomCount - snapshot.measuredRoomCount, "room")} without a size yet` : ""}.`
      : "Draw the floor plan, or simply add the rooms you are renovating and their sizes.",
    action: hasRooms ? { tab: "scope", label: "Review rooms" } : { tab: "scope", label: "Add the rooms" },
  };

  const scope: ActivationStep = {
    id: "scope",
    title: "The renovation work",
    state: hasScope ? "done" : "todo",
    detail: hasScope
      ? `${plural(snapshot.scopeItemCount, "work item")} planned.`
      : "Choose the work each room needs — a room package fills in a typical renovation in one step.",
    action: { tab: "scope", label: hasScope ? "Review the work" : "Plan the work" },
  };

  const unpriced = Math.max(0, snapshot.scopeItemCount - snapshot.pricedScopeItemCount);
  const estimate: ActivationStep = {
    id: "estimate",
    title: "Your renovation budget",
    state: estimated ? "done" : "todo",
    detail: estimated
      ? `${money(snapshot.estimateCents)} estimated across ${plural(snapshot.pricedScopeItemCount, "priced item")}${unpriced > 0 ? `; ${plural(unpriced, "item")} still need a quantity or a price` : ""}.`
      : hasScope
        ? `None of the ${plural(snapshot.scopeItemCount, "work item")} is priced yet — each needs a quantity (or a room size) to price.`
        : "Appears as soon as the work has a quantity and a price.",
    action: { tab: "scope", label: estimated ? "Open the estimate" : "Price the work" },
  };

  const bids: ActivationStep = {
    id: "bids",
    title: "Contractor bids",
    state: snapshot.bidPackageCount + snapshot.quoteCount > 0 ? "done" : "optional",
    detail:
      snapshot.bidPackageCount + snapshot.quoteCount > 0
        ? `${plural(snapshot.bidPackageCount, "bid package")} sent · ${plural(snapshot.quoteCount, "quote")} recorded.`
        : "Send contractors a bid request built from your scope, then compare their prices with your estimate.",
    action: { tab: "bids", label: snapshot.quoteCount > 0 ? "Compare quotes" : "Create a bid request" },
  };

  const actuals: ActivationStep = {
    id: "actuals",
    title: "Actual costs",
    state: snapshot.actualEventCount > 0 ? "done" : "optional",
    detail:
      snapshot.actualEventCount > 0
        ? `${plural(snapshot.actualEventCount, "cost")} recorded.`
        : "Record receipts and invoices as the work happens to track spending against the budget.",
    action: { tab: "accounting", label: snapshot.actualEventCount > 0 ? "Open accounting" : "Record a cost" },
  };

  /* THE NEXT STEP. Setup runs in order, but nothing is a gate: a project
   * with priced scope has an estimate whether or not anybody typed an
   * address, and an advanced user who started in Scope & Estimate is never
   * sent back to draw. Rooms are not required once the work exists — work
   * can be described at the project level. */
  const setup: ActivationStep[] = [property, rooms, scope, estimate];
  let next: ActivationStep | null = null;
  if (!estimated) {
    if (!hasScope && !hasRooms) next = snapshot.hasAddress ? rooms : property;
    else if (!hasScope) next = scope;
    else next = estimate;
  } else if (!snapshot.hasAddress) {
    next = property;
  } else if (bids.state !== "done") {
    next = bids;
  } else if (actuals.state !== "done") {
    next = actuals;
  }
  if (!estimated && hasScope && !hasRooms) rooms.state = "optional";
  if (next) next.state = "next";

  // Later steps stay "optional" until the estimate exists — they are what
  // comes after value, never a condition of it.
  return {
    steps: [...setup, bids, actuals],
    next,
    firstEstimateReached: estimated,
    setupDone: setup.filter((s) => s.state === "done").length,
    setupTotal: setup.length,
  };
}
